import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useGetSharedArticle } from "controller/hooks/kb/useKb";
import { useGetBrandingByDomainName } from "controller/hooks/branding/useBrandingByDomainName";
import { useEffect, useMemo } from "react";
import TipTapEditor from "components/editor/TipTapEditor";
import logoBig from "assets/logo/deskdayLogo.svg";
import { Error404Component } from "components";

export const SharedArticle = () => {
  const { article_id } = useParams<{ article_id: string }>();
  const theme = useTheme();

  const {
    data: articleData,
    isLoading: isArticleLoading,
    error: articleError,
  } = useGetSharedArticle(article_id || "", !!article_id);

  const {
    mutateAsync: getBranding,
    data: brandingData,
    isLoading: isBrandingLoading,
  } = useGetBrandingByDomainName();

  useEffect(() => {
    const domainName = window.location.hostname;
    getBranding(domainName);
  }, [getBranding]);

  const logoUrl = useMemo(() => {
    const logoItem = brandingData?.find(
      (item: any) => item.branding_type === "logo",
    );
    const wordmarkItem = brandingData?.find(
      (item: any) => item.branding_type === "wordmark",
    );

    const logo = logoItem?.url;
    const wordmark = wordmarkItem?.url;

    if (logo && wordmark) {
      return { logo, wordmark, showBoth: true };
    } else if (logo) {
      return { logo, showBoth: false };
    } else if (wordmark) {
      return { wordmark, showBoth: false };
    }
    // Fallback to DeskDay logo
    return { logo: logoBig, showBoth: false };
  }, [brandingData]);

  // Set favicon and tab title from branding
  useEffect(() => {
    const prevTitle = document.title;

    const logoItem = brandingData?.find(
      (item: any) => item.branding_type === "logo",
    );
    const faviconUrl = logoItem?.fav_icon_url || logoItem?.url;

    if (faviconUrl) {
      let link = document.querySelector(
        "link[rel~='icon']",
      ) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }

    // Set tab title from brand_name
    const brandNameItem = brandingData?.find(
      (item: any) => item.branding_type === "brand_name",
    );
    if (brandNameItem?.brand_name) {
      document.title = brandNameItem.brand_name;
    }

    return () => {
      document.title = prevTitle;
    };
  }, [brandingData]);

  if (isArticleLoading || isBrandingLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: theme.palette.neutral["005"],
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (articleError || !articleData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme.palette.neutral["005"],
        }}
      >
        <Error404Component
          title="Article not found"
          subtitle="The article you're looking for doesn't exist or has been removed."
          showCta={false}
        />
        {/* <Stack spacing={2} alignItems="center">
          <Typography variant="h4" color={theme.palette.lightBg.high}>
            Article Not Found
          </Typography>
          <Typography
            variant="body-medium"
            color={theme.palette.lightBg.medium}
          >
            The article you're looking for doesn't exist or has been removed.
          </Typography>
        </Stack> */}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.neutral["005"],
        p: "64px 96px",
        minHeight: "100vh",
      }}
    >
      {/* Article Content */}

      {/* TipTap Editor (Read-only) */}
      <Box
        sx={{
          backgroundColor: "#FFF",
          borderRadius: "4px",
          p: "40px 80px",
          boxShadow: "-4px 9px 11px 0px #00000014",
          minHeight: "calc(100vh - 128px)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            py: "16px",
            px: "24px",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {logoUrl.showBoth ? (
              <>
                <img
                  src={logoUrl.logo}
                  alt="Company Logo"
                  style={{
                    height: "40px",
                    objectFit: "contain",
                  }}
                />
                <img
                  src={logoUrl.wordmark}
                  alt="Company Wordmark"
                  style={{
                    height: "32px",
                    objectFit: "contain",
                  }}
                />
              </>
            ) : logoUrl.logo ? (
              <img
                src={logoUrl.logo}
                alt="Company Logo"
                style={{
                  height: "40px",
                  objectFit: "contain",
                }}
              />
            ) : (
              <img
                src={logoUrl.wordmark}
                alt="Company Wordmark"
                style={{
                  height: "32px",
                  objectFit: "contain",
                }}
              />
            )}
          </Stack>
        </Box>
        <Box>
          <TipTapEditor
            content={articleData.content}
            isEditable={false}
            forceFirstNodeH1={false}
            displayMode={true}
          />
        </Box>
      </Box>
    </Box>
  );
};
